import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { i18n } from 'i18n';
import { AssignerUnits } from './useAssignerUnits';
import {
  AutoPipeAssignerResult,
  SpoolClass,
  StopeRow,
  StopeRowStatus
} from './types';

interface AutoPipeAssignerResultsProps {
  result: AutoPipeAssignerResult;
  units: AssignerUnits;
}

const STATUS_COLOURS: Record<StopeRowStatus, 'success' | 'info' | 'error'> = {
  passedUnchanged: 'success',
  passedWithChanges: 'info',
  failed: 'error',
};

const statusLabel = (status: StopeRowStatus): string => ({
  passedUnchanged: i18n._('Passes unchanged'),
  passedWithChanges: i18n._('Passes with changes'),
  failed: i18n._('Failed'),
})[status];

const classLabel = (spoolClass: SpoolClass): string => (spoolClass === 'borehole' ? i18n._('Borehole') : i18n._('Level'));

const reasonText = ({ reason, blockingSpoolLabel, blockingClass }: StopeRow): string | undefined => {
  const spool = blockingSpoolLabel ?? '';
  switch (reason) {
    case 'noRoute': return i18n._('No active route from the backfill plant.');
    case 'missingStopePipe': return i18n._('The stope\'s pipe arrays use a pipe type that is not in the model.');
    case 'noStopePipeArrays': return i18n._('The stope has no pipe arrays.');
    case 'unreachable': return i18n._('No mix within the dataset\'s ranges reaches the stope at this pump pressure.');
    case 'boosterLimit': return i18n._('A booster pump on the route is over its limit.');
    case 'upgradeUnreachable': return i18n._('Upgrading {spool} leaves no mix that reaches the stope at this pump pressure.', { spool });
    case 'dischargePipe': return i18n._('The stope discharge pipe is overpressured.');
    case 'noHigherPipe': return blockingClass === 'borehole'
      ? i18n._('No higher-rated Borehole pipe for {spool}.', { spool })
      : i18n._('No higher-rated Level pipe for {spool}.', { spool });
    case 'didNotConverge': return i18n._('The pipe changes did not settle.');
    default: return undefined;
  }
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 0.5 }}>{children}</Typography>
);

const AutoPipeAssignerResults = ({ result, units }: AutoPipeAssignerResultsProps) => {
  const { formatNumber, formatLength, formatPumpPressureKpa } = units;
  const percent = (fraction: number | undefined, decimalPlaces: number) => (
    fraction !== undefined && Number.isFinite(fraction) ? `${formatNumber(fraction * 100, decimalPlaces)} %` : '–'
  );
  const passedCount = result.stopes.filter(({ status }) => status !== 'failed').length;

  return (
    <Box>
      <Typography variant="body2">
        {i18n._('{passed} of {total} stopes pass.', { passed: passedCount, total: result.stopes.length })}
        {' '}
        {i18n._('{count, plural, one {# spool changes pipe.} other {# spools change pipe.}}', { count: result.changes.length })}
      </Typography>

      <SectionTitle>{i18n._('Stopes')}</SectionTitle>
      <TableContainer sx={{ maxHeight: 260, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{i18n._('Stope')}</TableCell>
              <TableCell>{i18n._('Outcome')}</TableCell>
              <TableCell align="right">{i18n._('Mass Concentration')}</TableCell>
              <TableCell align="right">{i18n._('Pump Pressure')}</TableCell>
              <TableCell align="right">{i18n._('Peak % of pipe rating')}</TableCell>
              <TableCell>{i18n._('Reason')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.stopes.map((row) => (
              <TableRow key={row.stopeId}>
                <TableCell>{row.stopeName}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" color={STATUS_COLOURS[row.status]} label={statusLabel(row.status)} />
                </TableCell>
                <TableCell align="right">{percent(row.concentrationByMass, 1)}</TableCell>
                <TableCell align="right">{row.pumpPressureKpa !== undefined ? formatPumpPressureKpa(row.pumpPressureKpa) : '–'}</TableCell>
                <TableCell align="right">{percent(row.peakShareOfRating, 0)}</TableCell>
                <TableCell>{reasonText(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SectionTitle>{i18n._('Spool changes')}</SectionTitle>
      {result.changes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{i18n._('No spool needs a different pipe.')}</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 300, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{i18n._('Spool')}</TableCell>
                <TableCell>{i18n._('Class')}</TableCell>
                <TableCell align="right">{i18n._('Length')}</TableCell>
                <TableCell>{i18n._('From')}</TableCell>
                <TableCell>{i18n._('To')}</TableCell>
                <TableCell align="right">{i18n._('Peak pressure / rating (kPa)')}</TableCell>
                <TableCell>{i18n._('On the route of')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.changes.map((change) => {
                const overRating = change.peakPressureKpa !== undefined && change.peakPressureKpa >= change.toRatingKpa;
                return (
                  <TableRow key={change.spoolId}>
                    <TableCell>{change.label}</TableCell>
                    <TableCell>{classLabel(change.spoolClass)}</TableCell>
                    <TableCell align="right">{formatLength(change.length)}</TableCell>
                    <TableCell>{change.fromPipeName}</TableCell>
                    <TableCell>{change.toPipeName}</TableCell>
                    <TableCell align="right" sx={overRating ? { color: 'error.main' } : undefined}>
                      {`${change.peakPressureKpa !== undefined ? formatNumber(change.peakPressureKpa, 0) : '–'} / ${formatNumber(change.toRatingKpa, 0)}`}
                    </TableCell>
                    <TableCell>{change.stopeNames.join(', ')}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {result.totals.length > 0 && (
        <>
          <SectionTitle>{i18n._('New pipe totals')}</SectionTitle>
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n._('Pipe')}</TableCell>
                  <TableCell align="right">{i18n._('Total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.totals.map((total) => (
                  <TableRow key={total.pipeId}>
                    <TableCell>{total.pipeName}</TableCell>
                    <TableCell align="right">{formatLength(total.metres)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default AutoPipeAssignerResults;
